import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useConnectionStore } from '@/stores/connectionStore'
import { useLastConnection } from '@/hooks/useLastConnection'
import { useAppSettings } from '@/hooks/useAppSettings'
import { LoginPage } from '@/pages/LoginPage'
import { OAuthCallback } from '@/pages/OAuthCallback'
import { MainLayout } from '@/components/layout/MainLayout'
import { ConnectionsPage } from '@/pages/ConnectionsPage'
import { DatabaseExplorer } from '@/pages/DatabaseExplorer'
import { QueryEditor } from '@/pages/QueryEditor'
import { UserManagement } from '@/pages/UserManagement'
import { DatabaseUserManagement } from '@/pages/DatabaseUserManagement'
import { RBACMatrix } from '@/pages/RBACMatrix'
import { SessionsManager } from '@/pages/SessionsManager'
import { SystemInformation } from '@/pages/SystemInformation'
import { Settings } from '@/pages/Settings'
import { AgentsPage } from '@/pages/AgentsPage'
import { QueryProvider } from '@/components/common/QueryProvider'
import { SettingsProvider } from '@/contexts/SettingsContext'
import QueryEditorErrorBoundary from '@/components/query/QueryEditorErrorBoundary'
import { useEffect } from 'react'
import { preloadMonacoEditor } from '@/utils/monacoPreload'

// Component references to avoid JSX as props
const LoginPageComponent = LoginPage
const OAuthCallbackComponent = OAuthCallback
const NavigateToLogin = () => <Navigate to="/login" replace />
const NavigateToHome = () => <Navigate to="/" replace />
const MainLayoutComponent = MainLayout
const SmartRedirectComponent = SmartRedirect
const ConnectionsPageComponent = ConnectionsPage
const DatabaseExplorerComponent = DatabaseExplorer
const QueryEditorWithBoundary = () => (
  <QueryEditorErrorBoundary>
    <QueryEditor />
  </QueryEditorErrorBoundary>
)
const UserManagementComponent = UserManagement
const DatabaseUserManagementComponent = DatabaseUserManagement
const RBACMatrixComponent = RBACMatrix
const SessionsManagerComponent = SessionsManager
const SystemInformationComponent = SystemInformation
const SettingsComponent = Settings
const AgentsPageComponent = AgentsPage

function App() {
  const { user, loadUserFromStorage } = useAuthStore()
  
  // Load user from storage on app start and preload Monaco
  useEffect(() => {
    loadUserFromStorage()
    // Preload Monaco Editor after a short delay to not block initial render
    setTimeout(() => {
      preloadMonacoEditor()
    }, 1000)
  }, [loadUserFromStorage])

  if (!user) {
    return (
      <div className="h-full">
        <QueryProvider>
          <Routes>
            <Route path="/login" Component={LoginPageComponent} />
            <Route path="/auth/callback" Component={OAuthCallbackComponent} />
            <Route path="/dashboard" Component={OAuthCallbackComponent} />
            <Route path="*" Component={NavigateToLogin} />
          </Routes>
        </QueryProvider>
      </div>
    )
  }

  return (
    <div className="h-full">
      <QueryProvider>
        <SettingsProvider>
          <AuthenticatedApp />
        </SettingsProvider>
      </QueryProvider>
    </div>
  )
}

function AuthenticatedApp() {
  // Initialize app settings for authenticated user
  useAppSettings()
  
  // Restore last connection after user is authenticated
  useLastConnection()

  return (
    <div className="h-full">
      <Routes>
        <Route path="/" Component={MainLayoutComponent}>
        <Route index Component={SmartRedirectComponent} />
        <Route path="connections" Component={ConnectionsPageComponent} />
        <Route path="explorer/:connectionId" Component={DatabaseExplorerComponent} />
        <Route path="query/:connectionId" Component={QueryEditorWithBoundary} />
        <Route path="users" Component={UserManagementComponent} />
        <Route path="db-users/:connectionId" Component={DatabaseUserManagementComponent} />
        <Route path="rbac-matrix/:connectionId" Component={RBACMatrixComponent} />
        <Route path="sessions/:connectionId" Component={SessionsManagerComponent} />
        <Route path="system/:connectionId" Component={SystemInformationComponent} />
        <Route path="settings" Component={SettingsComponent} />
        <Route path="agents" Component={AgentsPageComponent} />
      </Route>
      <Route path="/login" Component={NavigateToHome} />
      <Route path="*" Component={NavigateToHome} />
      </Routes>
    </div>
  )
}

function SmartRedirect() {
  const { activeConnection } = useConnectionStore()
  
  // If there's an active connection, redirect to explorer, otherwise to connections
  if (activeConnection) {
    return <Navigate to={`/explorer/${activeConnection.id}`} replace />
  }
  
  return <Navigate to="/connections" replace />
}

export default App
